import React, { useRef } from 'react';
import { Button, CircularProgress, Tooltip, Box } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useExcelImport, ExcelFieldValue } from '../../../hooks/useExcelImport';
import { ImageValue } from '../../../models/Template';

/**
 * Styled button for Excel actions with consistent styling.
 */
const ExcelButton = styled(Button)(({ theme }) => ({
    borderRadius: '25px',
    height: '32px',
    paddingLeft: '15px',
    paddingRight: '15px',
    fontWeight: 600,
    textTransform: 'none',
    color: 'white',
    marginLeft: '8px',
    backgroundColor: '#1976d2',
    '&:hover': {
        backgroundColor: '#1565c0',
    },
    '&.Mui-disabled': {
        backgroundColor: '#90caf9',
        color: 'rgba(255, 255, 255, 0.7)',
    },
}));

interface ExcelImportExportProps {
    /** The template ID for Excel operations */
    templateId: string;
    /** Callback when fields are successfully imported from Excel */
    onFieldsImported: (fields: Record<string, string | ImageValue>) => void;
}

/**
 * Component that provides Excel import/export functionality for templates.
 *
 * Displays two buttons:
 * - Download Excel: Downloads a template Excel file with form fields
 * - Import from Excel: Uploads a filled Excel file to populate template fields
 *
 * Only visible when the user's team has Excel support enabled.
 */
const ExcelImportExport: React.FC<ExcelImportExportProps> = ({
    templateId,
    onFieldsImported
}) => {
    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const {
        canUseExcel,
        isDownloading,
        isUploading,
        downloadTemplate,
        uploadAndParse
    } = useExcelImport();

    // Don't render if team doesn't have Excel support
    if (!canUseExcel) {
        return null;
    }

    /**
     * Handles the download button click.
     */
    const handleDownload = () => {
        downloadTemplate(templateId);
    };

    /**
     * Handles the upload button click by triggering the file input.
     */
    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    /**
     * Handles file selection and parses the Excel file.
     */
    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const parsedFields = await uploadAndParse(templateId, file);
        if (parsedFields) {
            // Convert parsed fields to the format expected by the template editor
            const selections: Record<string, string | ImageValue> = {};

            for (const [key, value] of Object.entries(parsedFields)) {
                if (value.type === 'text') {
                    selections[key] = value.value || '';
                } else if (value.type === 'asset') {
                    selections[key] = {
                        // The sheet carries the asset id (excel_template_service
                        // _asset_to_dict). Keep it: it is how deriveImageFit
                        // tells an imported row from the template's default.
                        assetId: value.assetId,
                        name: value.name || '',
                        highRes: value.highRes || '',
                        thumbnail: value.thumbnail || '',
                        width: value.width,
                        height: value.height,
                        type: value.assetType,
                        duration: value.duration
                    };
                }
            }

            onFieldsImported(selections);
        }

        // Reset file input so the same file can be selected again
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
            {/* Hidden file input for Excel upload */}
            <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept=".xlsx"
                onChange={handleFileChange}
            />

            {/* Download Excel Template Button */}
            <Tooltip title={t('excel.tooltip.download')}>
                <span>
                    <ExcelButton
                        onClick={handleDownload}
                        disabled={isDownloading}
                        startIcon={
                            isDownloading ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : (
                                <FileDownloadIcon />
                            )
                        }
                    >
                        {t('excel.buttons.download')}
                    </ExcelButton>
                </span>
            </Tooltip>

            {/* Import from Excel Button */}
            <Tooltip title={t('excel.tooltip.import')}>
                <span>
                    <ExcelButton
                        onClick={handleUploadClick}
                        disabled={isUploading}
                        startIcon={
                            isUploading ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : (
                                <FileUploadIcon />
                            )
                        }
                    >
                        {t('excel.buttons.import')}
                    </ExcelButton>
                </span>
            </Tooltip>
        </Box>
    );
};

export default ExcelImportExport;
